import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { peep_client } from '~/src/api/peep';
import { useAbort } from '~src/utils/useAbort';
import { Button, TextInput, Text, Paper, Stack, Title, Alert, PasswordInput } from '@mantine/core';
import { AlertCircle } from 'lucide-react';
import { HTTPError } from 'ky';
import { ApiErrorResponseSchema, UserHint } from '~src/types/errors';

type ResetState =
  | { status: 'idle'; token: string; newPassword: string; confirmPassword: string }
  | { status: 'submitting'; token: string; newPassword: string; confirmPassword: string }
  | { status: 'error'; token: string; newPassword: string; confirmPassword: string; error: string };

export default function ResetPassword() {
  const [state, setState] = useState<ResetState>({
    status: 'idle',
    token: '',
    newPassword: '',
    confirmPassword: '',
  });
  const navigate = useNavigate();
  const abortRef = useAbort();

  const updateToken = (token: string) => {
    setState((s) =>
      s.status === 'submitting'
        ? s
        : { status: 'idle', token, newPassword: s.newPassword, confirmPassword: s.confirmPassword },
    );
  };

  const updateNewPassword = (newPassword: string) => {
    setState((s) =>
      s.status === 'submitting'
        ? s
        : { status: 'idle', token: s.token, newPassword, confirmPassword: s.confirmPassword },
    );
  };

  const updateConfirmPassword = (confirmPassword: string) => {
    setState((s) =>
      s.status === 'submitting' ? s : { status: 'idle', token: s.token, newPassword: s.newPassword, confirmPassword },
    );
  };

  const submit = async () => {
    if (state.status === 'submitting') return;

    const { token, newPassword, confirmPassword } = state;

    if (newPassword !== confirmPassword) {
      setState({ status: 'error', token, newPassword, confirmPassword, error: 'Passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setState({
        status: 'error',
        token,
        newPassword,
        confirmPassword,
        error: 'Password must be at least 8 characters',
      });
      return;
    }

    setState({ status: 'submitting', token, newPassword, confirmPassword });

    try {
      await peep_client.users.resetPassword(token, newPassword, { signal: abortRef.current.signal });
      navigate('/signin');
    } catch (e) {
      console.error(e);

      let errorMsg = 'An error occurred. Please try again.';

      if (e instanceof HTTPError) {
        try {
          const body = await e.response.json();
          const parsed = ApiErrorResponseSchema.parse(body);

          if (parsed.message === UserHint.InvalidToken) {
            errorMsg = 'Invalid or expired reset code. Please request a new one.';
          } else if (parsed.message) {
            errorMsg = parsed.message;
          }
        } catch {
          // Use default error
        }
      }

      setState({ status: 'error', token, newPassword, confirmPassword, error: errorMsg });
    }
  };

  const isLoading = state.status === 'submitting';
  const error = state.status === 'error' ? state.error : null;
  const canSubmit =
    state.token && state.newPassword && state.confirmPassword && (state.status === 'idle' || state.status === 'error');

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        minHeight: '100dvh',
        backgroundColor: 'var(--global-gray-lighter-7)',
        padding: '2rem',
      }}
    >
      <Paper
        style={{
          width: 'min(400px, 90vw)',
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid #f0f0f0',
        }}
      >
        <Stack gap="lg">
          <div style={{ textAlign: 'center' }}>
            <Title order={2} style={{ fontFamily: 'Lato', color: '#333', marginBottom: '0.5rem' }}>
              Set New Password
            </Title>
            <Text size="sm" style={{ fontFamily: 'Lato', color: '#666' }}>
              Enter the code from your email and choose a new password
            </Text>
          </div>

          {error && (
            <Alert icon={<AlertCircle size={16} />} color="red" style={{ fontFamily: 'Lato' }}>
              {error}
            </Alert>
          )}

          <Stack gap="md">
            <TextInput
              placeholder="Enter reset code (e.g. abcd-efgh)"
              value={state.token}
              onChange={(e) => updateToken(e.target.value)}
              disabled={isLoading}
              size="md"
              styles={{
                input: {
                  fontFamily: 'Lato',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                },
              }}
            />

            <PasswordInput
              placeholder="Enter new password"
              value={state.newPassword}
              onChange={(e) => updateNewPassword(e.target.value)}
              disabled={isLoading}
              size="md"
              styles={{
                input: {
                  fontFamily: 'Lato',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                },
              }}
            />

            <PasswordInput
              placeholder="Confirm new password"
              value={state.confirmPassword}
              onChange={(e) => updateConfirmPassword(e.target.value)}
              disabled={isLoading}
              size="md"
              styles={{
                input: {
                  fontFamily: 'Lato',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                },
              }}
            />
          </Stack>

          <Stack gap="sm">
            <Button
              onClick={submit}
              disabled={!canSubmit}
              loading={isLoading}
              size="md"
              style={{
                backgroundColor: 'var(--global-main)',
                fontFamily: 'Lato',
                fontWeight: '600',
                borderRadius: '8px',
              }}
              fullWidth
            >
              Reset Password
            </Button>

            <Button
              onClick={() => navigate('/signin')}
              variant="subtle"
              size="md"
              style={{
                fontFamily: 'Lato',
                color: 'var(--global-main)',
              }}
              fullWidth
            >
              Back to Sign In
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </div>
  );
}
